// Is video arriving from this camera right now? (ENG-2068, SUP-713)
//
// The camera status "connected" flag is CameraStatus able_to_connect_on_launch:
// for Milestone and other VMS integrations it only says the SERVER answered
// when the connector launched. A camera behind a reachable server can send no
// video at all and still read "Connected". The live per-camera signals come
// from actuate_admin `GET customer/{site_id}/camera_liveness/` (ENG-2046).
//
// Rules (Jacob, ENG-2068):
// - The signal is whether video is arriving, never motion. Plenty of healthy
//   cameras rarely see motion, so last-motion age is never a warning.
// - Only judged while the connector is running: fresh heartbeat AND either in
//   schedule or seen running by the census (a manual arm outside the schedule
//   is running; SUP-744).
// - A fresh bandwidth reading decides for any camera: > 0 kbps is receiving,
//   0 kbps is not receiving (Milestone JPEG pullers stream even with
//   milestone_motion, SUP-713).
// - Without one, continuous cameras are judged by the last frame's age.
//   Motion-gated cameras (`continuous: false`: site use_motion, Milestone
//   milestone_motion, SMTP/AutoPatrol/clips) legitimately stop reading between
//   motion, so stale evidence is never a warning for them.
// - No frame/bandwidth data for the camera: no inference at all.

/** Heartbeat cadence is ~5 min (site-status poller); two missed = stale.
 * Same threshold as the Troubleshooter (CONNECTOR_STALE_MINUTES). */
export const CONNECTOR_FRESH_MINUTES = 10;

/** Pullers re-upload the preview every 5 min, only when a new frame arrived;
 * three missed uploads on a continuous camera = video stopped. Same threshold
 * as the Troubleshooter (LAST_FRAME_STALE_MINUTES). */
export const VIDEO_FRESH_MINUTES = 15;

export interface CameraLivenessCamera {
  cameraId: number;
  cameraName: string;
  continuous: boolean;
  lastFrameAt: string | null;
  bandwidthUpdatedAt: string | null;
  inboundKbps: number | null;
}

export interface CameraLiveness {
  siteId: number;
  checkedAt: string | null;
  connector: {
    lastSeenAt: string | null;
    inSchedule: boolean | null;
    /** Heartbeat census: the connector was observed running. True after a
     * manual arm even when the schedule says off. */
    censusRunning?: boolean | null;
  } | null;
  cameras: CameraLivenessCamera[];
  errors: { source: string; detail: string }[];
}

/** Wire shape of camera_liveness/, the fields this page reads. */
export interface ApiCameraLiveness {
  site_id: number;
  checked_at?: string | null;
  connector?: {
    last_seen_at?: string | null;
    in_schedule?: boolean | null;
    census_running?: boolean | null;
  } | null;
  cameras?: {
    camera_id: number;
    camera_name: string;
    continuous?: boolean;
    last_frame_at?: string | null;
    bandwidth_updated_at?: string | null;
    inbound_bandwidth_kbps?: number | null;
  }[];
  errors?: { source: string; detail: string }[];
}

export const mapCameraLiveness = (body: ApiCameraLiveness): CameraLiveness => ({
  siteId: body.site_id,
  checkedAt: body.checked_at ?? null,
  connector: body.connector
    ? {
        lastSeenAt: body.connector.last_seen_at ?? null,
        inSchedule: body.connector.in_schedule ?? null,
        censusRunning: body.connector.census_running ?? null,
      }
    : null,
  cameras: (body.cameras ?? []).map((camera) => ({
    cameraId: camera.camera_id,
    cameraName: camera.camera_name,
    continuous: Boolean(camera.continuous),
    lastFrameAt: camera.last_frame_at ?? null,
    bandwidthUpdatedAt: camera.bandwidth_updated_at ?? null,
    inboundKbps: camera.inbound_bandwidth_kbps ?? null,
  })),
  errors: body.errors ?? [],
});

export type VideoFlowState =
  /** Fresh frame or non-zero bandwidth while the connector runs. */
  | "receiving"
  /** Connector running and a fresh 0 kbps reading (any camera), or a
   * continuous camera with stale video. */
  | "not_receiving"
  /** Connector not running or outside its schedule. Never a warning. */
  | "not_checked"
  /** No frame/bandwidth data for this camera (or motion-gated with no fresh
   * video): no inference, show the launch-time server check. */
  | "no_data"
  /** Liveness not loaded / failed / camera absent: today's display. */
  | "unavailable";

export interface VideoFlowStatus {
  state: VideoFlowState;
  /** Newest video evidence (ISO), when there is any. */
  lastVideoAt: string | null;
  /** True when the reason is a fresh 0 kbps reading. */
  zeroBandwidth: boolean;
}

const ageMinutes = (iso: string | null, nowMs: number): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return (nowMs - ms) / 60_000;
};

const newest = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(a) >= Date.parse(b) ? a : b;
};

/** The clock the payload was built against (server side), so ages are not
 * skewed by the viewer's clock; falls back to now. */
export const livenessNowMs = (
  liveness: CameraLiveness,
  fallbackMs: number = Date.now()
): number => {
  const checked = liveness.checkedAt ? Date.parse(liveness.checkedAt) : NaN;
  return Number.isNaN(checked) ? fallbackMs : checked;
};

export const videoFlowStatus = (
  liveness: CameraLiveness | null | undefined,
  cameraId: number,
  nowMs: number
): VideoFlowStatus => {
  const unavailable: VideoFlowStatus = {
    state: "unavailable",
    lastVideoAt: null,
    zeroBandwidth: false,
  };
  if (!liveness) return unavailable;
  const camera = liveness.cameras.find((row) => row.cameraId === cameraId);
  if (!camera) return unavailable;

  const lastVideoAt = newest(camera.lastFrameAt, camera.bandwidthUpdatedAt);
  const hasData =
    camera.lastFrameAt != null ||
    camera.bandwidthUpdatedAt != null ||
    camera.inboundKbps != null;
  if (!hasData)
    return { state: "no_data", lastVideoAt: null, zeroBandwidth: false };

  // Connector running? A heartbeat read that failed is "can't tell", not
  // "not armed": fall back to today's display.
  const lastSeenAt = liveness.connector?.lastSeenAt ?? null;
  if (!lastSeenAt) {
    const heartbeatFailed = liveness.errors.some(
      (error) => error.source === "heartbeat"
    );
    if (heartbeatFailed) return unavailable;
    return { state: "not_checked", lastVideoAt, zeroBandwidth: false };
  }
  const connectorAge = ageMinutes(lastSeenAt, nowMs);
  // Honor manual arms: the schedule alone says "off" for a site an operator
  // armed by hand, while the census sees the connector running (SUP-744).
  const runningBySchedule = liveness.connector?.inSchedule !== false;
  const runningByCensus = liveness.connector?.censusRunning === true;
  const connectorRunning =
    connectorAge != null &&
    connectorAge <= CONNECTOR_FRESH_MINUTES &&
    (runningBySchedule || runningByCensus);
  if (!connectorRunning) {
    return { state: "not_checked", lastVideoAt, zeroBandwidth: false };
  }

  const bandwidthAge = ageMinutes(camera.bandwidthUpdatedAt, nowMs);
  const bandwidthFresh =
    bandwidthAge != null && bandwidthAge <= VIDEO_FRESH_MINUTES;
  const frameAge = ageMinutes(camera.lastFrameAt, nowMs);
  const frameFresh = frameAge != null && frameAge <= VIDEO_FRESH_MINUTES;

  // A fresh bandwidth reading decides for ANY camera, motion-gated or not:
  // the Milestone JPEG puller streams all the time even with milestone_motion
  // and writes 0 kbps when the camera sends nothing (SUP-713, Lagoon Farm).
  if (bandwidthFresh && camera.inboundKbps != null) {
    return camera.inboundKbps > 0
      ? { state: "receiving", lastVideoAt, zeroBandwidth: false }
      : {
          state: "not_receiving",
          lastVideoAt: camera.lastFrameAt,
          zeroBandwidth: true,
        };
  }
  if (frameFresh) {
    return { state: "receiving", lastVideoAt, zeroBandwidth: false };
  }
  // Nothing fresh. Only a continuous camera is expected to send all the time.
  return camera.continuous
    ? { state: "not_receiving", lastVideoAt, zeroBandwidth: false }
    : { state: "no_data", lastVideoAt, zeroBandwidth: false };
};
